import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Alert,
} from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import DOMPurify from "dompurify";
import { useAuth } from "../contexts/AuthContext";
import { apiService, TermsResponse } from "../services/api";
import DrWeedyLogo from "../assets/DrWeedyLogo";
import TermsActions from "../components/TermsActions";
import { useReadToEnd } from "../hooks/useReadToEnd";

export default function TermsPage() {
  const { logout, acceptTerms } = useAuth();
  const [terms, setTerms] = useState<TermsResponse | null>(null);
  const [loadingTerms, setLoadingTerms] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState("");

  // Sanitiza o HTML dos termos vindos do backend antes de injetar via
  // dangerouslySetInnerHTML — defesa em profundidade contra XSS, caso o
  // conteúdo de `terms_of_use.content` seja adulterado ou editável por um
  // papel de menor confiança. `WHOLE_DOCUMENT: true` preserva o <style>
  // embutido (no <head> do HTML dos termos) que define a estilização.
  const sanitizedContent = useMemo(
    () =>
      terms ? DOMPurify.sanitize(terms.content, { WHOLE_DOCUMENT: true }) : "",
    [terms]
  );

  const { ref, atEnd, idle, onScroll, jumpToEnd, wake } = useReadToEnd(terms);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const data = await apiService.getTerms();
        setTerms(data);
      } catch {
        setError("Erro ao carregar os termos de uso");
      } finally {
        setLoadingTerms(false);
      }
    };
    fetchTerms();
  }, []);

  const handleAccept = async () => {
    try {
      setAccepting(true);
      setError("");
      const success = await acceptTerms();
      if (!success) {
        setError("Erro ao aceitar os termos. Tente novamente.");
      }
    } catch {
      setError("Erro ao aceitar os termos. Tente novamente.");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-petrolio-dark via-[#0a1a21] to-[#04090a] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-saluton/15 via-transparent to-transparent"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-petrolio/10 via-transparent to-transparent"></div>

      <div className="w-full flex flex-col relative z-10">
        <div className="flex items-center justify-between px-8 py-4">
          <div className="flex items-center gap-3">
            <DrWeedyLogo width={40} height={40} />
            <Typography
              variant="h6"
              sx={{ color: "#ffffff", fontWeight: 700, letterSpacing: "-0.02em", fontFamily: "Oxanium, sans-serif" }}
            >
              Dr. Weedy
            </Typography>
          </div>
          <Button
            onClick={logout}
            sx={{
              color: "rgba(255,255,255,0.6)",
              textTransform: "none",
              fontSize: "14px",
              "&:hover": { color: "#ffffff", backgroundColor: "rgba(255,255,255,0.05)" },
            }}
          >
            Sair
          </Button>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-4">
          <div className="max-w-3xl w-full">
            <div className="text-center mb-6">
              <Typography
                variant="h4"
                sx={{
                  color: "#ffffff",
                  fontWeight: 700,
                  marginBottom: "8px",
                  letterSpacing: "-0.03em",
                  fontFamily: "Oxanium, sans-serif",
                }}
              >
                Termos de Uso
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  fontSize: "15px",
                }}
              >
                Leia e aceite os termos para continuar utilizando a plataforma
              </Typography>
            </div>

            <Paper
              elevation={8}
              onPointerDown={wake}
              sx={{
                background:
                  "linear-gradient(135deg, rgba(26, 49, 60, 0.8) 0%, rgba(4, 9, 10, 0.9) 100%)",
                backdropFilter: "blur(20px)",
                border: "2px solid rgba(226, 114, 91, 0.2)",
                borderRadius: "24px",
                padding: "32px",
                boxShadow:
                  "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(226, 114, 91, 0.1)",
              }}
            >
              <Box className="space-y-4">
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      borderRadius: "16px",
                      background: "rgba(225, 100, 100, 0.15)",
                      border: "1px solid rgba(225, 100, 100, 0.3)",
                      color: "#e16464",
                      fontWeight: 500,
                    }}
                  >
                    {error}
                  </Alert>
                )}

                {loadingTerms ? (
                  <div className="flex justify-center py-20">
                    <CircularProgress sx={{ color: "#E2725B" }} />
                  </div>
                ) : terms ? (
                  <>
                    {terms.version && (
                      <div className="flex items-center justify-between">
                        <div className="inline-block px-3 py-1 bg-saluton/10 rounded-full border border-saluton/30 text-saluton-light text-xs font-medium">
                          Versão {terms.version}
                        </div>
                      </div>
                    )}

                    <div
                      ref={ref}
                      onScroll={onScroll}
                      className="overflow-y-auto pr-2"
                      style={{
                        maxHeight: "55vh",
                        scrollbarWidth: "thin",
                        scrollbarColor: "rgba(226, 114, 91, 0.3) transparent",
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                        style={{
                          color: "rgba(255,255,255,0.85)",
                          fontSize: "14px",
                          lineHeight: "1.7",
                        }}
                      />
                    </div>

                    <TermsActions
                      atEnd={atEnd}
                      idle={idle}
                      accepting={accepting}
                      acceptLabel="Aceitar Termos"
                      onAccept={handleAccept}
                      onJump={jumpToEnd}
                      secondary={
                        <Button
                          onClick={logout}
                          variant="outlined"
                          fullWidth
                          sx={{
                            borderRadius: "20px",
                            textTransform: "none",
                            fontSize: "16px",
                            fontWeight: 600,
                            padding: "14px 24px",
                            color: "rgba(255,255,255,0.7)",
                            borderColor: "rgba(255,255,255,0.2)",
                            "&:hover": {
                              borderColor: "rgba(225, 100, 100, 0.5)",
                              color: "#e16464",
                              backgroundColor: "rgba(225, 100, 100, 0.05)",
                            },
                          }}
                        >
                          Recusar
                        </Button>
                      }
                    />
                  </>
                ) : null}
              </Box>
            </Paper>
          </div>
        </div>
      </div>
    </div>
  );
}
